import useFetchHelpCenter from 'app/hooks/fetch/useFetchHelpCenter';
import useHalfOpenEnabled from 'app/hooks/useHalfOpenEnabled';
import useIsMobile from 'app/hooks/useIsMobile';
import useShouldAutoOpen from 'app/modules/tailor/hooks/useShouldAutoOpen';
import { useEffect, useMemo } from 'react';
import { useTailorHalfOpen } from 'app/hooks/useTailorHalfOpen';
import { useTailorHasOpened } from 'app/hooks/useTailorHasOpened';
import { useTailorIsOpen } from 'app/hooks/useTailorIsOpen';
import useAutoOpenEnabled from 'app/hooks/useAutoOpenEnabled';

import { useSuggestedArticle } from '../SuggestedArticleProvider';
import useManuallyClosed from './useManuallyClosed';

export default function useAutoOpenEffect() {
  const { setIsOpen } = useTailorIsOpen();
  const { setHasOpened } = useTailorHasOpened();
  const { setHalfOpen } = useTailorHalfOpen();
  const autoOpen = useAutoOpenEnabled();

  const { lastViewedArticle } = useSuggestedArticle();
  const halfOpenEnabled = useHalfOpenEnabled();
  const [shouldAutoOpen, , removeShouldAutoOpen] = useShouldAutoOpen();
  const [isManuallyClosed] = useManuallyClosed();
  const isMobile = useIsMobile();
  const { data: helpCenterData } = useFetchHelpCenter();

  const { forceOpen, qsAutoOpenDelay } = useMemo(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const qsAutoOpenDelay = queryParams.get('ostroAutoOpenDelay');
    const qsEngageHubOpen = queryParams.get('engagehubopen');
    const forceOpen = qsEngageHubOpen?.toLowerCase() === 'true';
    return { forceOpen, qsAutoOpenDelay };
  }, []);

  const delayKey = isMobile ? 'autoopen_mobile_delay_ms' : 'autoopen_desktop_delay_ms';
  const autoOpenDelay =
    qsAutoOpenDelay !== null ? parseInt(qsAutoOpenDelay, 10) : helpCenterData?.options?.[delayKey] ?? 0;

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;

    if (shouldAutoOpen === false && !forceOpen) {
      setIsOpen(false);
      removeShouldAutoOpen();
    } else if (forceOpen || ((shouldAutoOpen === true || autoOpen) && !isManuallyClosed)) {
      if (lastViewedArticle && !forceOpen) {
        setIsOpen(false);
      } else {
        timeout = setTimeout(() => {
          setIsOpen(true);
          setHasOpened(true);
          removeShouldAutoOpen();
          if (halfOpenEnabled) {
            setHalfOpen(true);
          }
        }, autoOpenDelay);
      }
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [autoOpen]); // eslint-disable-line react-hooks/exhaustive-deps
}
